import type { QueryKey } from "@tanstack/react-query";
import type { Permission } from "@tcg/api-client";
import type { ChangeMessage, ResyncReason, TopicStatus } from "@tcg/realtime";
import { useQueryBridge } from "@tcg/realtime/query";
import { useRealtime, useRealtimeClient } from "@tcg/realtime/react";
import { useMemo } from "react";

import { usePermissions } from "@/lib/auth/usePermissions";

import { allowedTopics, topicPermission } from "./topics";

export type { ChangeMessage, TopicStatus };

/**
 * Poll used when a screen cannot follow its topic at all (realtime switched off / the person may
 * not read that topic), when the screen gives none (design/07 §9.1 fallback).
 */
export const DEFAULT_POLL_MS = 30_000;

/**
 * What the screen's data follows: a {@link TopicStatus}, or `polled` — the topic is subscribed but
 * not live right now, and the tab's one realtime client covers it: `onResync("poll")` every 30 s
 * while in fallback ("○ Updates every 30 s" in the top bar) and `onResync("hub")` when it is back.
 * One fallback mechanism (T11-52 review): the screen then adds no `refetchInterval` of its own.
 */
export type LiveStatus = TopicStatus | "polled";

/** Poll interval for a screen whose topics are `status`: none while live or polled by the client. */
export function pollFor(status: LiveStatus, pollMs: number = DEFAULT_POLL_MS): number | false {
  return status === "live" || status === "polled" ? false : pollMs;
}

export interface LiveTopicsOptions {
  enabled?: boolean;
  /** Refetch what the screen shows: the stream missed messages / the tab came back / poll. */
  onResync?: (reason: ResyncReason) => void;
}

/**
 * Subscribe this screen to `topics` on the tab's one realtime connection (`@tcg/realtime`) —
 * only the topics the signed-in person may read (the hub refuses the others). `onChange` runs for
 * every change message; returns `live`, `polled` (not live, but the client's 30 s poll / reconnect
 * calls `onResync`) or `connecting` / `fallback` (nothing can follow it: the screen polls itself).
 */
export function useLiveTopics(
  topics: readonly string[],
  onChange: (message: ChangeMessage) => void,
  options: LiveTopicsOptions = {},
): LiveStatus {
  const { can } = usePermissions();
  const client = useRealtimeClient();
  const key = topics.join(",");
  const allowed = useMemo(() => allowedTopics(key ? key.split(",") : [], can), [key, can]);
  const enabled = (options.enabled ?? true) && allowed.length > 0;
  const status = useRealtime(allowed, onChange, {
    enabled,
    ...(options.onResync ? { onResync: options.onResync } : {}),
  });
  // subscribed through the tab's client: its 30 s poll / reconnect resync refresh the screen
  // (through onResync) — no second poll; not subscribable at all → the screen polls itself
  const subscribed = enabled && client !== null && Boolean(options.onResync);
  return subscribed && status !== "live" ? "polled" : status;
}

/**
 * R21 for a screen whose data lives under `keys`: a change on `topic` (`staff:customers`,
 * `staff:consignment` …) refetches them (batched 250 ms — 20 messages = 1 refetch); a resync / the
 * tab coming back / the fallback poll refetches them too. Returns the poll interval the screen's
 * queries use as `refetchInterval` (`false` while live). `match` narrows which messages count.
 */
export function useLiveInvalidate(
  topic: string,
  keys: readonly QueryKey[],
  options: {
    permission?: Permission;
    match?: (message: ChangeMessage) => boolean;
    pollMs?: number;
  } = {},
): number | false {
  const bridge = useQueryBridge();
  const { can } = usePermissions();
  const permission = options.permission ?? topicPermission(topic);
  const refresh = () => {
    bridge.invalidate(keys);
  };
  const status = useLiveTopics(
    [topic],
    (message) => {
      if (!options.match || options.match(message)) {
        refresh();
      }
    },
    { enabled: permission ? can(permission) : true, onResync: refresh },
  );
  return pollFor(status, options.pollMs);
}
